"use client";

import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading || !menuItems) {
    return <NavigationMenu viewport={false} className="flex bg-[#FDFDF5] rounded-md">
        <NavigationMenuList className="flex-wrap">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <NavigationMenuItem key={`skeleton-${i}`}>
              <div className="h-9 px-4 py-2 bg-muted-light/50 animate-pulse rounded-md w-20" />
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>;
  }
  return <NavigationMenu viewport={false} className="flex bg-[#FDFDF5] rounded-md">
      <NavigationMenuList className="flex-wrap gap-0.5">
        {menuItems.map((item, i) => <NavigationMenuItem key={`${item.id}-${i}`} data-index={i}>
            <NavigationMenuLink asChild className="relative px-3 py-2 transition-colors duration-[var(--duration-medium)] hover:bg-[#D4AF37] focus:bg-[#D4AF37]">
              <Link to={item.href || "#"} className="text-[#001F3F] font-medium text-sm">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  const handleLinkClick = (index: number) => {
    setClickedIndex(index);
    setTimeout(() => {
      setIsOpen(false);
      setClickedIndex(null);
    }, 200);
  };
  if (loading || !menuItems) {
    return <div className="md:hidden">
        <div className="h-9 w-9 bg-muted-light animate-pulse rounded-md" />
      </div>;
  }
  return <div className="md:hidden relative z-10">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent text-[hsl(210,100%,12%)]" aria-label="Toggle menu">
        {isOpen ? <X className="size-6 text-[hsl(210,100%,12%)]" /> : <Menu className="size-6 text-[hsl(210,100%,12%)]" />}
      </Button>

      {isOpen && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-in fade-in duration-300" onClick={() => setIsOpen(false)} aria-hidden="true" />
          
          <div className={`fixed top-0 left-0 h-full w-80 bg-[#FDFDF5] border-r border-border shadow-strong z-50 transition-transform duration-300 ease-[var(--ease-smooth)] pt-[calc(env(safe-area-inset-top)+2.25rem)] ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px) + 2.25rem, 2.25rem)'
      }}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-[var(--font-cinzel)] font-bold text-lg text-[#002147]">
                Menu
              </span>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="hover:bg-muted" aria-label="Close menu">
                <X className="size-5 text-[#002147]" />
              </Button>
            </div>
            
            <nav className="overflow-y-auto p-4" style={{
          height: 'calc(100% - 73px)'
        }}>
              <ul className="flex flex-col gap-2">
                {menuItems.map((item, i) => <li key={`${item.id}-${i}`} data-index={i}>
                    <Link to={item.href || "#"} className={`block text-[#001F3F] font-medium py-3 px-4 rounded-md transition-all duration-[var(--duration-medium)] ${clickedIndex === i ? "bg-[#D4AF37]/20 text-[#002147]" : "hover:bg-[#D4AF37]/10"}`} onClick={() => handleLinkClick(i)}>
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </nav>
          </div>
        </>}
    </div>;
}
function WhatsAppWidget() {
  const [showWelcome, setShowWelcome] = useState(false);
  const whatsappNumber = "971504585183";
  const preFilledMessage = encodeURIComponent("Hello Ancel, I would like to learn more about securing a Founding Family spot for my family legacy.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${preFilledMessage}`;
  return <>
      <div className="fixed bottom-4 right-8 sm:bottom-5 sm:right-5 z-[55] flex flex-col items-center gap-1">
        <span className="text-xs font-[var(--font-montserrat)] font-medium text-[#25D366] bg-white px-2 py-1 rounded-md shadow-soft">
          Whatsapp
        </span>
        <Button onClick={() => setShowWelcome(true)} className="size-12 sm:size-14 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-strong p-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-110 focus-visible:ring-[#25D366]/30" size="icon" aria-label="Chat with the Architect">
          <MessageCircle className="size-6 sm:size-7" />
        </Button>
      </div>

      {showWelcome && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowWelcome(false)}>
          <div className="bg-background rounded-lg shadow-strong max-w-md w-full p-4 sm:p-6 relative animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="icon" onClick={() => setShowWelcome(false)} className="absolute top-2 right-2 hover:bg-muted" aria-label="Close">
              <X className="size-4" />
            </Button>

            <div className="mb-4">
              <h3 className="font-[var(--font-cinzel)] font-bold text-xl text-[#d4af37] mb-1">
                Family Legacy Voices
              </h3>
              <p className="font-[var(--font-montserrat)] text-sm text-foreground-light">
                Typically responds within 24 hours
              </p>
            </div>

            <div className="mb-6">
              <p className="font-[var(--font-libre-baskerville)] text-base text-foreground-light leading-relaxed">
                Welcome. I am here to help you anchor your family's most precious stories. How can I assist you in starting your legacy journey today?
              </p>
            </div>

            <Button asChild className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-[var(--font-montserrat)] font-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:shadow-medium focus-visible:ring-[#25D366]/30">
              <Link to={whatsappLink} newTab className="flex items-center justify-center gap-2">
                <MessageCircle className="size-5" />
                Chat with the Architect
              </Link>
            </Button>
          </div>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#" || href === "/") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (path === "/" || path === window.location.pathname) {
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          const offset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => {
      document.removeEventListener("click", handleAnchorClick);
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);
  return <>
      <Link to="#secure-legacy" className="fixed top-0 left-0 right-0 w-full h-9 bg-[#002147] text-[#d4af37] flex items-center justify-center z-[9999] hover:bg-[#003366] transition-colors duration-[var(--duration-medium)] px-2 sm:px-4">
        <span className="font-[var(--font-montserrat)] text-[11px] sm:text-[13px] tracking-[1px] uppercase font-medium text-center">
          SPECIAL LAUNCH OFFER FOR 6 FOUNDING FAMILIES
        </span>
      </Link>

      <header className={`sticky top-9 w-full bg-[hsl(65,30%,98%)] border-b border-[hsl(0,0%,71%)] z-[9998] transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] mb-0 ${isScrolled ? "h-20 shadow-soft" : "h-24"}`}>
        <div className="w-full max-w-none px-6 lg:px-8 h-full flex items-center justify-between gap-4">
          <Link to="/" className="flex-shrink-0 bg-transparent px-3 py-2 rounded-md">
            <img src="https://familylegacyvoices.com/wp-content/uploads/2026/01/familylegacyvoices_love_website_header_logo-2.png" alt="Family Legacy Voices" className="h-7" />
          </Link>

          <MenuProvider menu_id="52" className="hidden md:flex bg-transparent">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="flex items-center gap-4 flex-shrink-0">
            <MenuProvider menu_id="52">
              <MobileMenu />
            </MenuProvider>
          </div>
        </div>
      </header>

      <WhatsAppWidget />
    </>;
}